/**
 * FindingCard — unified shape for lint / refactor / compliance findings
 * (PR F · §F.1.4).
 *
 * Severity bar (left), title + body + meta chips (centre), action stack
 * (right). Up to 2 actions show inline; the rest collapse into a ⋯
 * overflow menu. Muted findings dim themselves and replace the action
 * stack with a single Unmute button.
 */
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.js';

export type FindingSeverity = 'high' | 'med' | 'low';

export interface FindingAction {
  label: string;
  onClick: () => void;
  /** Visual treatment — defaults to ghost; primary for the main CTA. */
  variant?: 'primary' | 'ghost';
  /** R1.L5 — stable React key. Two actions with the same visible label
   *  (e.g. "Apply" on multiple findings) would collide on `key={label}`;
   *  callers can pass `id` to disambiguate. Falls back to `label`. */
  id?: string;
}

export interface FindingCardProps {
  title: string;
  body: string;
  severity: FindingSeverity;
  rule?: string;
  entityRefs?: string[];
  /** Epoch ms; when in the future, the card renders as muted. */
  mutedUntil?: number;
  onUnmute?: () => void;
  actions?: FindingAction[];
}

const INLINE_ACTIONS = 2;

export function FindingCard({
  title,
  body,
  severity,
  rule,
  entityRefs = [],
  mutedUntil,
  onUnmute,
  actions = [],
}: FindingCardProps): JSX.Element {
  const isMuted = mutedUntil !== undefined && mutedUntil > Date.now();
  const inline = actions.slice(0, INLINE_ACTIONS);
  const overflow = actions.slice(INLINE_ACTIONS);
  const [openMenu, setOpenMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close overflow menu on outside click.
  useEffect(() => {
    if (!openMenu) return;
    const onClick = (e: MouseEvent): void => {
      if (!menuRef.current?.contains(e.target as Node)) setOpenMenu(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openMenu]);

  return (
    <article
      className="finding-card"
      data-severity={severity}
      data-muted={isMuted ? 'true' : 'false'}
    >
      <div className={`finding-card-sev is-${severity}`} aria-hidden="true" />
      <div className="finding-card-content">
        <h4 className="finding-card-title">{title}</h4>
        <p className="finding-card-body">{body}</p>
        <div className="finding-card-meta">
          <span className={`chip ${severityChipTone(severity)}`}>{severity}</span>
          {rule && (
            <span className="chip">
              rule · <code>{rule}</code>
            </span>
          )}
          {entityRefs.map((id) => (
            <span key={id} className="finding-card-entity-pill">
              {id}
            </span>
          ))}
        </div>
      </div>
      <div className="finding-card-actions" ref={menuRef}>
        {isMuted && onUnmute && (
          <button type="button" className="btn btn-ghost" onClick={onUnmute}>
            Unmute
          </button>
        )}
        {!isMuted && inline.map((a) => (
          <button
            key={a.id ?? a.label}
            type="button"
            className={`btn ${a.variant === 'primary' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={a.onClick}
          >
            {a.label}
          </button>
        ))}
        {!isMuted && overflow.length > 0 && (
          <>
            <button
              type="button"
              className="btn btn-ghost finding-card-more"
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={openMenu}
              onClick={() => setOpenMenu((v) => !v)}
            >
              <Icon name="more_horiz" size="sm" />
            </button>
            {openMenu && (
              <div
                role="menu"
                className="finding-card-menu"
                aria-label="More actions"
              >
                {overflow.map((a) => (
                  <button
                    key={a.id ?? a.label}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpenMenu(false);
                      a.onClick();
                    }}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </article>
  );
}

function severityChipTone(sev: FindingSeverity): string {
  if (sev === 'high') return 'danger';
  if (sev === 'med') return 'warn';
  return 'info';
}
